import { CircularProgress, FormControl } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChangeEvent, FC, useState, useEffect, useRef } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "../../../index.css";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, X } from "lucide-react";
import { QUERY_KEY } from "@/constants";
import { listCityByCountryService } from "@/services/discount.service";
import useDebounce from "@/hooks/useDebounce";
import { Province } from "@/types";
import { useVirtualizer } from "@tanstack/react-virtual";

interface IProps {
  countryCode?: string;
  values?: string[];
  onChange: (value: string) => void;
  onCityDataChange?: (data: { provinces: Province[] } | null) => void;
  disabled?: boolean;
}

const SelectCity: FC<IProps> = ({
  onChange,
  values,
  countryCode,
  onCityDataChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [textSearch, setTextSearch] = useState<string>("");
  const query = useDebounce(textSearch, 300);
  const parentRef = useRef<HTMLDivElement>(null);

  const { data: cityData, isLoading } = useQuery({
    queryKey: [QUERY_KEY.listCityByCountry, countryCode],
    queryFn: () => listCityByCountryService(countryCode || ""),
    enabled: !!countryCode,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (cityData && onCityDataChange) {
      onCityDataChange?.(cityData);
    }
  }, [cityData]);

  const filteredCities =
    cityData?.provinces &&
    cityData?.provinces
      ?.flatMap((province) =>
        province.citys.filter((city) =>
          city.cityName?.toLowerCase().includes(query.toLowerCase()),
        ),
      )
      .sort((a, b) => (a.cityName || "").localeCompare(b.cityName || ""));

  const rowVirtualizer = useVirtualizer({
    count: filteredCities?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 30,
  });

  useEffect(() => {
    if (open) {
      rowVirtualizer.measure();
    }
  }, [open]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setTextSearch(event.target.value);
  };

  const handleSelectCity = (cityCode: string) => {
    onChange(cityCode);
  };

  const handleRemoveCity = (cityCode: string) => {
    onChange(cityCode);
  };

  const renderValue = () => {
    const selectedCities =
      cityData?.provinces &&
      cityData?.provinces?.flatMap((province) =>
        province.citys.filter((city) => values?.includes(city.cityCode)),
      );

    return (
      <div className="mt-1 flex flex-wrap items-center gap-2 py-1">
        {isLoading && (
          <div className="flex items-center justify-center py-2">
            <CircularProgress size="12px" color="inherit" />
          </div>
        )}
        {selectedCities?.length ? (
          selectedCities.map((city, index) => (
            <div
              key={index}
              className="flex max-w-full items-center gap-1 rounded-[4px] bg-[#E0E7FF] px-2 py-1"
            >
              <p className="line-clamp-1 text-wrap text-[10px] font-medium leading-4 text-[#322F80]">
                {city.cityName}
              </p>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRemoveCity(city.cityCode);
                }}
              >
                <X className="size-3 text-[#322F80]" />
              </div>
            </div>
          ))
        ) : (
          <span className="text-sm text-text">Select city</span>
        )}
      </div>
    );
  };

  return (
    <FormControl className="w-full">
      <Popover
        open={disabled ? false : open}
        onOpenChange={disabled ? () => {} : setOpen}
      >
        <PopoverTrigger asChild>
          <div
            className={cn(
              "flex items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-4 py-3",
              disabled
                ? "cursor-not-allowed bg-neutral-50 opacity-50"
                : "cursor-pointer",
            )}
          >
            <div>
              {values?.length ? (
                renderValue()
              ) : (
                <span className="text-sm text-text">Select city</span>
              )}
            </div>
            <KeyboardArrowDownIcon
              className=""
              sx={{
                color: "#94A3B8",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-full"
          style={{
            width: "var(--radix-popover-trigger-width)",
          }}
        >
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Search cities..."
              value={textSearch}
              onChange={handleSearch}
              className={cn(
                "placeholder:text-[#94A3B8], h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal",
              )}
            />
            <div
              className={cn("flex flex-col overflow-y-auto", {
                "h-[270px]": !isLoading,
              })}
              ref={parentRef}
            >
              {isLoading && (
                <div className="flex items-center justify-center py-2">
                  <CircularProgress size="22px" color="inherit" />
                </div>
              )}
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const city = filteredCities?.[virtualItem.index];
                  if (!city) return null;
                  return (
                    <div
                      key={virtualItem.key}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <div
                        key={city.cityCode}
                        className="flex cursor-pointer items-center rounded-md p-2 hover:bg-zinc-100"
                        onClick={() => {
                          handleSelectCity(city.cityCode);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            values?.includes(city.cityCode)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <span className="text-sm text-[#475569]">
                          {city.cityName}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* {filteredCities?.map((city) => (
                <div
                  key={city.cityCode}
                  className="flex cursor-pointer items-center rounded-md p-2 hover:bg-zinc-100"
                  onClick={() => {
                    handleSelectCity(city.cityCode);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      values?.includes(city.cityCode)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <span className="text-sm text-[#475569]">
                    {city.cityName}
                  </span>
                </div>
              ))} */}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};

export default SelectCity;
